import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import PageHeader from "@/components/common/PageHeader";
import StatCard from "@/components/dashboard/StatCard";
import { useLoggedInCompany } from "@/hooks/queries/useCompanyQueries";
import { useCourses } from "@/hooks/queries/useCourseQueries";
import { useDepartments } from "@/hooks/queries/useDepartmentQueries";
import type { User } from "@/hooks/useAuth";
import { useAuth } from "@/hooks/useAuth";
import {
  ChartBarIcon,
  DocumentTextIcon,
  TrophyIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { getAuthToken } from "@/utils/secureStorage";
import { ChangeEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useCompanyAdminDashboard } from "@/hooks/useCompanyAdminDashboard";

interface CompanyUser extends User {
  departments: Array<{ _id: string; name: string }>;
}

const CompanyAdminDashboard = (): ReactNode => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const companyUser = user as unknown as CompanyUser;
  const navigate = useNavigate();

  // Use custom hook for state management
  const {
    selectedPeriod,
    setSelectedPeriod,
    localDepartments,
  } = useCompanyAdminDashboard(user);

  const isDepartmentAdmin = user?.role === "department_admin";

  const departmentsQuery = useDepartments(companyUser?._id || "");
  const departments = Array.isArray(departmentsQuery.data)
    ? departmentsQuery.data
    : Array.isArray(localDepartments)
    ? localDepartments
    : [];


  // Use the company hook to fetch real employees
  const companyQuery = !isDepartmentAdmin ? useLoggedInCompany() : null;
  const {
    data: companyData,
    isLoading: companyLoading,
  } = companyQuery || {
    data: null,
    isLoading: false,
  };

  // Extract employees from company data - check both possible structures
  const apiEmployees = companyData?.data?.document?.employees || companyData?.employees || [];

  console.log("🔄 [COMPANY_DASHBOARD] Company data structure:", {
    hasCompanyData: !!companyData,
    companyDataKeys: companyData ? Object.keys(companyData) : null,
    hasDocument: !!companyData?.data?.document,
    documentKeys: companyData?.data?.document ? Object.keys(companyData.data.document) : null,
    employeesFromDocument: companyData?.data?.document?.employees?.length || 0,
    employeesFromRoot: companyData?.employees?.length || 0,
    totalEmployees: apiEmployees.length
  });

  // Count active users based on recent login (last 7 days)
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const activeUsers = apiEmployees.filter((emp: any) => {
    if (!emp.lastLogin) return false;
    const lastLoginDate = new Date(emp.lastLogin);
    return lastLoginDate >= sevenDaysAgo;
  }).length;

  // For both company admins and department admins, use apiEmployees
  const totalEmployees = apiEmployees.length;

  // Fetch all courses for the company
  const { data: allCourses = [], isLoading: coursesLoading } = useCourses();

  // Compute stats from real data
  const completedCourses = allCourses.filter(
    (c: any) => c.isActive && c.completionRate === 100
  ).length;
  // For averageScore and completionRate, you may need to adjust based on your backend data structure
  const averageScore =
    allCourses.length > 0
      ? Math.round(
          allCourses.reduce((sum, c: any) => sum + (c.averageScore || 0), 0) /
            allCourses.length
        )
      : 0;
  const completionRate =
    allCourses.length > 0
      ? Math.round(
          allCourses.reduce((sum, c: any) => sum + (c.completionRate || 0), 0) /
            allCourses.length
        )
      : 0;

  // Recent employees: take the last 5 employees from apiEmployees
  const recentEmployees = apiEmployees.slice(-5).reverse();
  // Recent courses: take the last 5 courses
  const recentCourses = allCourses.slice(-5).reverse();

  // Debug logging
  console.log("CompanyAdminDashboard loaded with:", {
    user: user,
    companyId: companyUser?._id,
    authToken: getAuthToken(),
    departments: user?.departments,
  });


  const handlePeriodChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedPeriod(e.target.value);
  };



  return (
    <div>
      <PageHeader
        title={t("admin.companyOverview")}
        description={
          user?.name
            ? `${t("admin.company")}: ${user.name}`
            : t("admin.companyDescription")
        }
        action={
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/profile?edit=true")}
              className="w-full sm:w-auto text-xs sm:text-sm whitespace-nowrap"
            >
              {t("company.editCompany", "Edit Company")}
            </Button>
            <select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              className="rounded-md border border-gray-300 bg-white py-2 px-3 text-xs sm:text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white w-full sm:w-auto"
            >
              <option value="week">{t("common.thisWeek")}</option>
              <option value="month">{t("common.thisMonth")}</option>
              <option value="quarter">{t("common.thisQuarter")}</option>
              <option value="year">{t("common.thisYear")}</option>
            </select>
          </div>
        }
      />

      {/* Stats Overview */}
      <div className="mt-4 sm:mt-6 grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-2 xl:grid-cols-3">
        <StatCard
          title={t("admin.totalEmployees")}
          value={companyLoading ? t("common.loading") : totalEmployees}
          icon={<UsersIcon className="h-6 w-6" />}
          change={{ value: 5, isPositive: true }}
        />
        <StatCard
          title={t("admin.completedCourses")}
          value={coursesLoading ? t("common.loading") : completedCourses}
          icon={<DocumentTextIcon className="h-6 w-6" />}
          change={{ value: 12, isPositive: true }}
        />
        <StatCard
          title={t("admin.averageScore")}
          value={coursesLoading ? t("common.loading") : `${averageScore}%`}
          icon={<ChartBarIcon className="h-6 w-6" />}
          change={{ value: 3, isPositive: true }}
        />
        <StatCard
          title={t("admin.activeUsers")}
          value={companyLoading ? t("common.loading") : activeUsers}
          icon={<UsersIcon className="h-6 w-6" />}
          change={{ value: 4, isPositive: true }}
        />
        <StatCard
          title={t("admin.completionRate")}
          value={coursesLoading ? t("common.loading") : `${completionRate}%`}
          icon={<TrophyIcon className="h-6 w-6" />}
          change={{ value: 2, isPositive: true }}
        />
      </div>

      <div className="mt-6 sm:mt-8 grid gap-4 sm:gap-6 lg:grid-cols-2">
        {/* Recent Employees Table */}
        <Card>
          <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {t("admin.recentEmployees")}
            </h2>
            <Link to="/admin/employees">
              <Button variant="outline" size="sm" className="w-full sm:w-auto text-xs sm:text-sm">
                {t("common.viewAll")}
              </Button>
            </Link>
          </div>
          <div className="mt-4 overflow-x-auto -mx-2 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-2 sm:px-4 py-3 sm:py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t("common.employee")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t("common.department")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-3.5 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t("common.coursesCompleted")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-3.5 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t("common.status")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentEmployees.map((employee) => (
                  <tr
                    key={employee._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="whitespace-nowrap px-2 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-900 dark:text-white max-w-[120px] truncate">
                      {employee.fullName || (employee as any).name}
                    </td>
                    <td className="whitespace-nowrap px-2 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-[100px] truncate">
                      {departments.find((d) => d._id === employee.departmentId)
                        ?.name || "-"}
                    </td>
                    <td className="whitespace-nowrap px-2 sm:px-4 py-3 sm:py-4 text-right text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {(employee as any).coursesCompleted || "-"}
                    </td>
                    <td className="whitespace-nowrap px-2 sm:px-4 py-3 sm:py-4 text-right text-xs sm:text-sm">
                      <span
                        className={`inline-flex rounded-full px-1.5 sm:px-2 text-[10px] sm:text-xs font-semibold leading-5 ${
                          employee.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                        }`}
                      >
                        {employee.isActive ? "active" : "inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Recent Courses Table */}
        <Card>
          <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {t("admin.recentCourses")}
            </h2>
            <Button variant="outline" size="sm" className="w-full sm:w-auto text-xs sm:text-sm">
              {t("common.viewAll")}
            </Button>
          </div>
          <div className="mt-4 overflow-x-auto -mx-2 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-2 sm:px-4 py-3 sm:py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t("common.course")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-3.5 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t("common.enrollments")}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-3.5 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t("common.completionRate")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentCourses.map((course) => (
                  <tr
                    key={course._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="whitespace-nowrap px-2 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-900 dark:text-white max-w-[150px] truncate">
                      {typeof course.title === 'string' ? course.title : course.title?.en || ""}
                    </td>
                    <td className="whitespace-nowrap px-2 sm:px-4 py-3 sm:py-4 text-right text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {(course as any).enrollments || "-"}
                    </td>
                    <td className="whitespace-nowrap px-2 sm:px-4 py-3 sm:py-4 text-right text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {(course as any).completionRate !== undefined
                        ? `${(course as any).completionRate}%`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Department table moved to its own page (Departments.tsx) */}
    </div>
  );
};

export default CompanyAdminDashboard;
